import ClipboardJS from 'clipboard';
export interface ThrottleOptions {
  runFirst?: boolean;
}

/**
 * 节流器
 * @param param0
 * @returns
 */
export const ussThrottle = ({ runFirst = false }: ThrottleOptions = {}) => {
  let _timer: any = 0;
  return (callback: (...args: any[]) => void, args: any[], timeout = 100) => {
    if (_timer) {
      clearTimeout(_timer);
    } else if (runFirst) {
      callback(...args);
      _timer = setTimeout(() => { }, 0);
      return;
    }

    _timer = setTimeout(() => {
      callback(...args);
      _timer = 0;
    }, timeout);
  };
};

/**
 * 版本对比
 * @param newVer 新版本
 * @param oldVer 旧版本
 * @returns
 */
export function compareVersion(newVer: string | undefined, oldVer: string) {
  if (!newVer) {
    return -1;
  }
  const v1 = newVer.split('.');
  const v2 = oldVer.split('.');
  const len = Math.max(v1.length, v2.length);

  while (v1.length < len) {
    v1.push('0');
  }
  while (v2.length < len) {
    v2.push('0');
  }

  for (let i = 0; i < len; i++) {
    const num1 = parseInt(v1[i]);
    const num2 = parseInt(v2[i]);

    if (num1 > num2) {
      return 1;
    } else if (num1 < num2) {
      return -1;
    }
  }

  return 0;
}

export function printAllProperties(obj: any) {
  let currentObj = obj;
  while (currentObj) {
    Object.getOwnPropertyNames(currentObj).forEach(prop => {
      console.log(`Property: ${prop}, Value: ${currentObj[prop]}`);
    });
    currentObj = Object.getPrototypeOf(currentObj);
  }
}


/**
 * 检查一个字符串是否是有效的JSON格式。
 *
 * @param str 需要检查的字符串。
 * @returns 如果是有效的JSON则返回 true，否则返回 false。
 */
export function isJson(str: string): boolean {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

/**
 * 类型检测工具函数集合
 */

/**
 * 检测是否为字符串
 * @param value 要检测的值
 * @returns 是否为字符串
 */
export function isString(value: any): value is string {
  return typeof value === 'string';
}

/**
* 检测是否为数字
* @param value 要检测的值
* @returns 是否为数字
*/
export function isNumber(value: any): value is number {
  return typeof value === 'number';
}

/**
* 检测是否为布尔值
* @param value 要检测的值
* @returns 是否为布尔值
*/
export function isBoolean(value: any): value is boolean {
  return typeof value === 'boolean';
}

/**
* 检测是否为对象（不包括数组和函数，以及 null）
* @param value 要检测的值
* @returns 是否为对象
*/
export function isObject(value: any): value is Record<string, any> {
  return value !== null && typeof value === 'object' && !Array.isArray(value) && typeof value !== 'function';
}

/**
* 检测是否为数组
* @param value 要检测的值
* @returns 是否为数组
*/
export function isArray(value: any): value is any[] {
  return Array.isArray(value);
}

/**
* 检测是否为函数
* @param value 要检测的值
* @returns 是否为函数
*/
export function isFunction(value: any): value is Function {
  return typeof value === 'function';
}

/**
* 检测是否为 null
* @param value 要检测的值
* @returns 是否为 null
*/
export function isNull(value: any): value is null {
  return value === null;
}

/**
* 检测是否为 undefined
* @param value 要检测的值
* @returns 是否为 undefined
*/
export function isUndefined(value: any): value is undefined {
  return value === undefined;
}

/**
* 检测是否为 Date 对象
* @param value 要检测的值
* @returns 是否为 Date 对象
*/
export function isDate(value: any): value is Date {
  return value instanceof Date;
}

// 数组去重，如果是对象可以指定属性
type DedupeConfig = string | ((item: any) => string | number);
export function deduplicateArray<T>(array: T[], config?: DedupeConfig): T[] {
  if (!Array.isArray(array)) {
    throw new Error('Input must be an array.');
  }

  if (array.length <= 1) {
    return array;
  }

  const uniqueSet = new Set();

  const result = array.filter(item => {
    let key: string | number;

    if (config) {
      // 如果提供了配置并且是函数，那么调用它获取键值
      if (typeof config === 'function') {
        key = config(item);
      } else {
        // 如果是字符串，尝试从对象中获取对应的属性作为键值
        if (typeof item === 'object' && item !== null) {
          if (config in item) {
            key = item[config as keyof typeof item];
          } else {
            throw new Error(`Field "${config}" not found in object.`);
          }
        } else {
          throw new Error('Provided configuration field but input is not an object array.');
        }
      }
    } else {
      // 如果没有提供配置，直接使用元素本身作为键值
      key = item as unknown as string | number;
    }

    // 如果 set 中还没有这个键值，添加进去并返回 true，表示保留该项
    if (!uniqueSet.has(key)) {
      uniqueSet.add(key);
      return true;
    }
    // 否则返回 false，表示过滤掉该项
    return false;
  });

  return result;
};

// 赋值元素内容
export function copyElementContent(
  elementId: string,
  successCallback: (message: string) => void,
  errorCallback: (errorMessage: string) => void
): void {
  // 获取目标元素
  const targetElement = document.getElementById(elementId);
  if (!targetElement) {
    errorCallback('目标元素不存在');
    return;
  }

  // 获取元素内容（文本或 value 值）
  const textToCopy: string = targetElement.textContent || targetElement.value;

  // 创建临时按钮作为触发器
  const tempButton: HTMLButtonElement = document.createElement('button');
  document.body.appendChild(tempButton);

  // 初始化 ClipboardJS
  const clipboard: ClipboardJS = new ClipboardJS(tempButton, {
    text: () => textToCopy
  });

  // 监听成功事件
  clipboard.on('success', (e: ClipboardJS.Event) => {
    successCallback(`成功复制文本: ${textToCopy}`);
    // 清理临时元素和实例
    clipboard.destroy();
    document.body.removeChild(tempButton);
  });

  // 监听错误事件
  clipboard.on('error', (e: ClipboardJS.Event) => {
    errorCallback('复制失败，请检查权限或浏览器支持');
    // 清理临时元素和实例
    clipboard.destroy();
    document.body.removeChild(tempButton);
  });

  // 触发临时按钮的点击事件
  tempButton.click();
}
